'use client';

import { useState } from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { CheckIcon, ChevronsUpDownIcon, Settings2Icon } from 'lucide-react';
import { useI18n } from '@/providers/i18n';

type InlineComboboxSelectProps = {
  id: string;
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Show a search input in the dropdown. Defaults to true when there are many options. */
  searchable?: boolean;
  /** Show the leading settings icon. Defaults to true. */
  showIcon?: boolean;
};

export function InlineComboboxSelect({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
  searchable,
  showIcon = true,
}: InlineComboboxSelectProps) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;
  const showSearch = searchable ?? options.length > 8;
  return (
    <div className="flex items-center min-w-0 h-9">
      <Combobox
        open={open}
        onOpenChange={(next) => { if (!disabled) setOpen(next); }}
        data={options.map((o) => ({ label: o.label, value: o.value }))}
        type="inline-option"
        value={value}
        onValueChange={(val) => { onChange(val); setOpen(false); }}
      >
        <ComboboxTrigger
          id={id}
          variant="ghost"
          className="rounded-xl h-9 pl-2.5 pr-3 hover:bg-sidebar-accent transition-all border-none font-medium"
          disabled={disabled}
        >
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2 overflow-hidden">
              {showIcon ? <Settings2Icon size={16} className="shrink-0 text-muted-foreground" /> : null}
              <span className="truncate">{label}</span>
              <span className="truncate text-muted-foreground">{selectedLabel}</span>
            </div>
            <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
          </div>
        </ComboboxTrigger>
        <ComboboxContent
          className="p-0"
          title={t('{{type}} Selector', { type: label })}
          popoverOptions={{ className: 'fm-toolbar-surface w-[340px] max-w-[90vw] rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}
        >
          {showSearch && <ComboboxInput className="h-9" placeholder={t('Search')} />}
          <ComboboxList className="custom-scrollbar max-h-[24rem]">
            <ComboboxEmpty />
            <ComboboxGroup className="p-1">
              {options.map((o) => (
                <ComboboxItem
                  key={o.value}
                  value={o.value}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  className="rounded-lg py-2 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2"
                >
                  {o.label}
                  <CheckIcon
                    className={cn(
                      'ml-auto size-4 text-primary',
                      value === o.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
